import { z } from "zod";

// Lead sources and the tracking numbers behind them. The list of sources is
// the company's own (it's also the website form's "how did you hear about
// us", RequestFormSettings.howHeardOptions). A tracking number is a real
// local phone number rented for one source: it rings the company's own line,
// and every call is logged under the source. Written by functions only.

export const MAX_TRACKING_NUMBERS = 10;

/** tenants/{t}/trackingNumbers/{id} */
export const TrackingNumberSchema = z.object({
  source: z.string().min(1).max(80),
  /** E.164, the number on the ad. */
  phone: z.string(),
  /** The phone company's id for it. */
  sid: z.string(),
  /** E.164: the company line these calls ring. */
  forwardTo: z.string(),
  /** Say which source it is to the office before connecting. */
  whisper: z.boolean().default(true),
  /** Pop the caller on the office's screens while it rings (Setup → Lead sources). */
  pop: z.boolean().default(true),
  /** How long the company's line rings before the caller hears we couldn't take the call. A main line's voicemail needs to win this race. */
  ringSeconds: z.number().int().min(10).max(60).default(25),
  /** E.164: outside the company's working hours (Setup → Clevis Ring) calls ring this instead, the on-call phone. null = the same line around the clock. */
  afterHoursForwardTo: z.string().nullable().default(null),
  status: z.enum(["active", "released"]).default("active"),
  calls: z.number().int().nonnegative().default(0),
  lastCallAt: z.number().nullable().default(null),
  createdByUid: z.string(),
  createdAt: z.number(),
  releasedAt: z.number().nullable().default(null),
});
export type TrackingNumber = z.infer<typeof TrackingNumberSchema>;

/** tenants/{t}/calls/{callSid}: one call to a tracking number. */
export const CallLogSchema = z.object({
  numberId: z.string(),
  source: z.string(),
  from: z.string(),
  to: z.string(),
  forwardTo: z.string(),
  /** Matched by phone to a customer record, when there is one. */
  customerId: z.string().nullable().default(null),
  customerName: z.string().default(""),
  status: z.enum(["ringing", "answered", "missed", "busy", "failed"]).default("ringing"),
  durationSec: z.number().int().nonnegative().default(0),
  startedAt: z.number(),
  /** When the office picked up (the phone company tells us); null until then, or if nobody did. */
  answeredAt: z.number().nullable().default(null),
  endedAt: z.number().nullable().default(null),
  /** Whether this call pops on screen: the number's setting when it rang. */
  pop: z.boolean().default(true),
  /** It came outside working hours and rang the number's after-hours line. */
  afterHours: z.boolean().default(false),
});
export type CallLog = z.infer<typeof CallLogSchema>;

// ---------- Clevis Ring: the phone side as a product ----------
// Numbers, the main line forwarded in, the caller pop and the call log are
// Clevis Ring. The carrier charges behind it (a number per month, a forwarded
// minute) are passed through to the company at the platform's rates, so a
// company turns Ring on by agreeing to that and keeping a card on file, like
// a hotel's incidentals card on a comped room. The super admin can cap a
// company's numbers and minutes (blank = no limit); hitting a minute cap never
// drops a call unless the company's hard stop is on.

/** appConfig/ring: the pass-through rates (super admin). */
export const RingRatesSchema = z.object({
  numberMonthlyCents: z.number().int().nonnegative().default(150),
  minuteCents: z.number().int().nonnegative().default(3),
  updatedAt: z.number().optional(),
});
export type RingRates = z.infer<typeof RingRatesSchema>;

// Working hours. A phone system rings the office by day and the on-call phone
// by night; a forwarded main line needs the same, or an evening call rings an
// empty office. The company sets one weekly schedule in its own time zone, and
// each number can name an after-hours line. No after-hours line = that number
// rings the same place around the clock.
export const RING_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type RingDay = (typeof RING_DAYS)[number];
export const RING_DAY_LABELS: Record<RingDay, string> = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const HHMM = z.string().regex(/^\d{2}:\d{2}$/);
export const RingDayHoursSchema = z.object({ open: z.boolean().default(true), start: HHMM.default("08:00"), end: HHMM.default("17:00") });
export type RingDayHours = z.infer<typeof RingDayHoursSchema>;
const WEEKDAY: RingDayHours = { open: true, start: "08:00", end: "17:00" };
const WEEKEND: RingDayHours = { open: false, start: "08:00", end: "17:00" };
export const RingHoursSchema = z.object({
  /** Off: every number rings its usual line around the clock. */
  enabled: z.boolean().default(false),
  /** IANA zone the hours are in ("America/Los_Angeles"). Required once enabled. */
  timezone: z.string().max(60).default(""),
  mon: RingDayHoursSchema.default(WEEKDAY),
  tue: RingDayHoursSchema.default(WEEKDAY),
  wed: RingDayHoursSchema.default(WEEKDAY),
  thu: RingDayHoursSchema.default(WEEKDAY),
  fri: RingDayHoursSchema.default(WEEKDAY),
  sat: RingDayHoursSchema.default(WEEKEND),
  sun: RingDayHoursSchema.default(WEEKEND),
});
export type RingHours = z.infer<typeof RingHoursSchema>;

/** The company's wall clock at an instant: which day, and "HH:MM" (24 h). null when the zone isn't one the runtime knows. */
export function clockIn(timezone: string, ms: number): { day: RingDay; hhmm: string } | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(ms));
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    const day = get("weekday").slice(0, 3).toLowerCase() as RingDay;
    if (!RING_DAYS.includes(day)) return null;
    const hour = get("hour") === "24" ? "00" : get("hour");
    return { day, hhmm: `${hour.padStart(2, "0")}:${get("minute").padStart(2, "0")}` };
  } catch {
    return null;
  }
}

/** Whether the office is open at this instant by the company's schedule. Hours off, no zone, or an unknown zone = open (nothing is ever sent after-hours by accident). */
export function isOpenAt(hours: RingHours, ms: number): boolean {
  if (!hours.enabled || !hours.timezone) return true;
  const clock = clockIn(hours.timezone, ms);
  if (!clock) return true;
  const day = hours[clock.day];
  return day.open && clock.hhmm >= day.start && clock.hhmm < day.end;
}

/** tenants/{t}/settings/ring: the company's agreement, with the rates it saw, and its working hours. */
export const RingSettingsSchema = z.object({
  agreedAt: z.number().nullable().default(null),
  agreedByUid: z.string().default(""),
  agreedByEmail: z.string().default(""),
  rates: RingRatesSchema.default({ numberMonthlyCents: 150, minuteCents: 3 }),
  hours: RingHoursSchema.default({}),
  updatedAt: z.number().optional(),
});
export type RingSettings = z.infer<typeof RingSettingsSchema>;

/** Per-company caps on the tenant doc (super admin). null = no limit. */
export const RingLimitsSchema = z.object({
  numbers: z.number().int().min(0).max(100).nullable().default(null),
  minutesPerMonth: z.number().int().min(0).max(1_000_000).nullable().default(null),
  /** Off by default: at the minute cap, calls still go through and people get told. On: callers hear the line can't take calls. */
  hardStop: z.boolean().default(false),
});
export type RingLimits = z.infer<typeof RingLimitsSchema>;

/** What the super admin can fill in with one click. */
export const RING_TIER_DEFAULTS: Record<string, { numbers: number; minutesPerMonth: number }> = {
  journeyman: { numbers: 3, minutesPerMonth: 1000 },
  master: { numbers: 10, minutesPerMonth: 5000 },
};

/** tenants/{t}/ringUsage/{YYYY-MM}: forwarded minutes this month (functions write at the end of each call). */
export const RingUsageSchema = z.object({
  month: z.string(),
  minutes: z.number().int().nonnegative().default(0),
  calls: z.number().int().nonnegative().default(0),
  alerted80At: z.number().nullable().default(null),
  alerted100At: z.number().nullable().default(null),
  updatedAt: z.number(),
});
export type RingUsage = z.infer<typeof RingUsageSchema>;

/** tenants/{t}/ringInvoices/{YYYY-MM}: one month's pass-through charge and what happened to it. */
export const RingInvoiceSchema = z.object({
  month: z.string(),
  numbers: z.number().int().nonnegative(),
  minutes: z.number().int().nonnegative(),
  numberMonthlyCents: z.number().int().nonnegative(),
  minuteCents: z.number().int().nonnegative(),
  amountCents: z.number().int().nonnegative(),
  status: z.enum(["paid", "failed", "no_card", "nothing"]),
  stripeInvoiceId: z.string().nullable().default(null),
  error: z.string().default(""),
  createdAt: z.number(),
});
export type RingInvoice = z.infer<typeof RingInvoiceSchema>;

/** The usage month a moment falls in (UTC, "2026-11"). */
export const ringMonthKey = (ms: number) => new Date(ms).toISOString().slice(0, 7);

/** A month's charge at the given rates. */
export function ringChargeCents(numbers: number, minutes: number, rates: { numberMonthlyCents: number; minuteCents: number }): number {
  return numbers * rates.numberMonthlyCents + minutes * rates.minuteCents;
}
